import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './DashboardCard.module.css';
import {
  casaConstants,
  ROUTES_PATH,
  toastMessageConstants
} from '../../../../constants/globalConstant';
import { isSieraDashboard, teamsViewCardItems } from '../../../../utils/casaUtils';
import LsqStatusBadge from '../../../../components/LsqStatusBadge';
import { WebDashboardCardMenu } from './WebDashboardCardMenu';
import { DashboardCardMenu } from './DashboardCardMenu';
import { ReactComponent as Preview } from '../../../../assets/svgIcons/lsq_preview.svg';
import { ReactComponent as Siera } from '../../../../assets/svgIcons/siera.svg';
import { IconButton } from '@lsq/nextgen/v2/button';
import { fetchPublishedPreviewSections } from '../utils/homepagePreviewUtils';
import analytics from '../../../../utils/analytics';
import useToast from '../../../../hooks/useToast';

/**
 * Unified Dashboard Card Component for Team Management
 * Used in both Mobile Teams and Web Teams views
 *
 * @param {Object} props
 * @param {Object} props.dashboard - Dashboard/homepage data
 * @param {Function} props.setDialog - Dialog state setter
 * @param {Function} props.setPreviewPanel - Preview panel state setter (mobile only)
 * @param {string} props.teamId - Team ID
 * @param {string} props.variant - "mobile" | "web" - Controls layout and behavior
 */
export const DashboardCard = ({
  dashboard,
  setDialog,
  setPreviewPanel,
  teamId,
  variant = 'web'
}) => {
  const navigate = useNavigate();
  const toast = useToast();

  // Normalize data structure - mobile uses different field names
  const id = dashboard?.id || dashboard?.homepageId;
  const name = dashboard?.name || dashboard?.homepageName;
  const status = dashboard?.status;
  const cloneDashboardId = dashboard?.cloneDashboardId;
  const clonedDashboard = dashboard?.clonedDashboard;
  const hasDraftChanges = dashboard?.hasDraftChanges;

  // Calculate resolved status
  const resolvedStatus =
    variant === 'web'
      ? status === casaConstants.PUBLISHED &&
        cloneDashboardId &&
        clonedDashboard?.updatedAt !== clonedDashboard?.createdAt
        ? casaConstants.UNPUBLISHED_CHANGES
        : status
      : status === casaConstants.PUBLISHED && hasDraftChanges
        ? casaConstants.UNPUBLISHED_CHANGES
        : status;

  // Web variant: navigate to builder
  const navigateToDashBoard = () => {
    navigate(`${ROUTES_PATH.webHomepageBuilder}/${id}`);
  };

  // Mobile variant: open preview panel
  const previewIconClicked = () => {
    if (variant === 'mobile' && setPreviewPanel) {
      setPreviewPanel({
        state: true,
        loading: true,
        data: {
          title: name,
          homepage: dashboard,
          sections: []
        }
      });
      fetchPublishedPreviewSections(id)
        .then((publishedViewSections) => {
          setPreviewPanel((prev) => ({
            ...prev,
            loading: false,
            data: {
              ...prev.data,
              sections: publishedViewSections
            }
          }));
        })
        .catch(() => {
          setPreviewPanel({
            state: false,
            loading: false,
            data: null
          });
          toast.error(toastMessageConstants.FAILED_TO_FETCH_HOMEPAGE);
        });
      analytics.sendEvent('homeapge_preview_tap_from_teams_view', {
        homepageId: id,
        status: status,
        homepageName: name
      });
    }
  };

  const previewButtonHandler = () => {
    if (variant === 'web') {
      navigateToDashBoard();
    } else {
      previewIconClicked();
    }
  };

  // Render mobile variant (compact, inline actions)
  if (variant === 'mobile') {
    return (
      <div
        className={`flex relative ${styles['dashboard-card-container']}`}
        data-testid="teams-view-dashboard-card">
        <LsqStatusBadge status={resolvedStatus} variant="dot" />
        <span title={name}>{name}</span>
        <div className={styles['dashboard-card-actions']}>
          <IconButton
            data-testid="homepage-preview"
            onClick={previewIconClicked}
            icon={<Preview />}
            size="xs"
            variant="secondary-gray"
          />
          <DashboardCardMenu
            items={teamsViewCardItems(resolvedStatus)}
            homepage={dashboard}
            setDialog={setDialog}
            teamId={teamId}
          />
        </div>
      </div>
    );
  }

  // Render web variant (same appearance as mobile, different menu component)
  return (
    <div
      className={`flex relative ${styles['dashboard-card-container']}`}
      data-testid="teams-view-dashboard-card">
      <LsqStatusBadge status={resolvedStatus} variant="dot" />
      <div className={styles['dashboard-card-content']}>
        {isSieraDashboard(dashboard) && (
          <Siera style={{ width: '1rem', height: '1rem', flexShrink: 0 }} />
        )}
        <span title={name}>{name}</span>
      </div>
      <div className={styles['dashboard-card-actions']}>
        {!isSieraDashboard(dashboard) && (
          <IconButton
            data-testid="homepage-preview"
            onClick={previewButtonHandler}
            icon={<Preview />}
            size="xs"
            variant="secondary-gray"
          />
        )}
        <WebDashboardCardMenu
          items={teamsViewCardItems(resolvedStatus)}
          homepage={dashboard}
          setDialog={setDialog}
          teamId={teamId}
        />
      </div>
    </div>
  );
};
